'use client';

import { useSearchParams } from 'next/navigation';
import { memo, useCallback, useEffect, useState } from 'react';
import { MarketoFormElement } from './components/marketo-form-element';
import { MarketoStylesheets } from './components/marketo-stylesheets';
import { MarketoThankYou } from './components/marketo-thank-you';
import { useMarketoForm } from './hooks/use-marketo-form';
import './marketo-form.scss';
import type { MarketoFormProps } from './marketo-form.types';

function isValidUrl(value: string | undefined): value is string {
  if (!value) return false;
  if (value.startsWith('/')) return true;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

export const MarketoFormExperience = memo((props: MarketoFormProps) => {
  const { formContent, thankYouUrl: rawThankYouUrl } = props;
  const thankYouUrl = isValidUrl(rawThankYouUrl) ? rawThankYouUrl : undefined;
  const searchParams = useSearchParams();
  const [submitted, setSubmitted] = useState(false);

  // Success handler — redirect to URL if provided, otherwise show thank-you state
  const handleFormSuccess = useCallback(() => {
    if (thankYouUrl) {
      window.location.href = thankYouUrl;
    } else {
      setSubmitted(true);
    }
  }, [thankYouUrl]);

  const { formId, resetForm } = useMarketoForm({ ...props, onFormSuccess: handleFormSuccess });

  // Legacy: aliId in URL means Marketo did a full-page redirect (fallback)
  const aliId = searchParams.get('aliId');
  const stateParam = searchParams.get('state');
  const showThankYou = submitted || stateParam === 'thank-you';

  useEffect(() => {
    if (aliId) {
      if (thankYouUrl) {
        window.location.href = thankYouUrl;
        return;
      }

      const params = new URLSearchParams(searchParams.toString());
      if (params.get('state') !== 'thank-you') {
        params.set('state', 'thank-you');
      }
      params.delete('aliId');

      const newUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [aliId, searchParams, thankYouUrl]);

  const handleGoBack = useCallback(() => {
    resetForm();
    setSubmitted(false);

    // Clean URL params without triggering navigation
    const params = new URLSearchParams(searchParams.toString());
    params.delete('aliId');
    params.delete('state');

    const newUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;
    window.history.replaceState({}, '', newUrl);
  }, [searchParams, resetForm]);

  return (
    <>
      <MarketoStylesheets />
      {showThankYou && <MarketoThankYou onGoBack={handleGoBack} formContent={formContent} />}
      <div style={showThankYou ? { display: 'none' } : undefined}>
        <MarketoFormElement formId={formId} />
      </div>
    </>
  );
});

MarketoFormExperience.displayName = 'MarketoFormExperience';
